import { Button, Input, Modal, Spin, Tabs, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { ReactSVG } from 'react-svg';
import { DAY_NAME, PAGE_SIZE, PAGE_SIZE_OPTIONS, ROLES, TYPE_PAGE } from '../../constants';
import { selectAllCompanies } from '../../modules/companies/stores/slice';
import { doGetCompanies } from '../../modules/companies/stores/thunk';
import { selectAllDeliveryAreas } from '../../modules/delivery-areas/stores/slice';
import { doGetAllDeliveryAreas } from '../../modules/delivery-areas/stores/thunk';
import { selectAllLocations } from '../../modules/locations/stores/slice';
import { doCreateLocation, doGetLocations, doUpdateLocation } from '../../modules/locations/stores/thunk';
import { selectAllUsers } from '../../modules/users/store/slice';
import { doGetUsers } from '../../modules/users/store/thunk';
import LocationForm from '../Forms/Companies/Location';
import CompaniesFilter from '../Forms/Filters/CompaniesFilter';
import CompaniesTable from '../Tables/CompaniesTable';
import withPage from './withPage';
import './Companies.scss';
import { DeliveryAreaModal } from '../Modals/DeliveryAreaModal';
import { CalendarOutlined } from '@ant-design/icons';
import { OffSchedule } from '../Inputs/OffSchedule';
import usePageView from '../../hooks/usePageView';

const CompaniesPage = ({ query, pushTo }) => {
  const dispatch = useDispatch();

  const isFetchingCompany = useSelector((state) => state.company.isFetching);
  const isFetchingLocation = useSelector((state) => state.location.isFetching);
  const actionLocation = useSelector((state) => state.location.action);
  const statusLocation = useSelector((state) => state.location.status);
  const metadata = useSelector((state) => state.location.metadata);
  const companies = useSelector((state) => selectAllCompanies(state));
  const locations = useSelector((state) => selectAllLocations(state));
  const deliveryAreas = useSelector((state) => selectAllDeliveryAreas(state));
  const users = useSelector((state) => selectAllUsers(state));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deliveryAreaModalOpen, setDeliveryAreaModalOpen] = useState(false);
  const [offScheduleOpen, setOffScheduleOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [typeLocation, setTypeLocation] = useState(false);
  const [locationDetail, setLocationDetail] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const currentPage = Number(query.current) || 1;
  const itemsPerPage = Number(query.pageSize) || PAGE_SIZE;

  // Emit page view event
  usePageView(window.location.href);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    locale: { items_per_page: 'Company Locations/Page' },
    total: metadata?.totalItems || 0,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Company Locations`,
  };

  useEffect(() => {
    dispatch(doGetLocations({ page: currentPage, take: itemsPerPage, ...query }));
    dispatch(doGetCompanies({ page: 1, take: 1000 }));
    dispatch(doGetUsers({ page: 1, take: 1000, role: ROLES.LOCATION_USER }));
    dispatch(doGetAllDeliveryAreas());
  }, []);

  useEffect(() => {
    if (['create', 'update'].includes(actionLocation) && statusLocation === 'succeeded') {
      dispatch(doGetLocations({ page: currentPage, take: itemsPerPage, ...query }));
      cancelModal();
    }
  }, [isFetchingLocation]);

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetLocations({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const handleActionLocation = (data) => {
    const params = {
      locationId: locationDetail?.locationId,

      companyId: data.companyId,
      locationType: data.locationType,
      name: data.name,
      address: data.address,
      region: data.region,
      addressLat: data.addressLat ? String(data.addressLat) : undefined,
      addressLng: data.addressLng ? String(data.addressLng) : undefined,
      contactPerson: data.contactPerson,
      phone: data.phone,
      supportEmail: data.supportEmail,
      isActive: data.isActive,
      mondayFrom: data.pickupHours?.mon?.start || null,
      mondayTo: data.pickupHours?.mon?.end || null,
      tuesdayFrom: data.pickupHours?.tue?.start || null,
      tuesdayTo: data.pickupHours?.tue?.end || null,
      wednesdayFrom: data.pickupHours?.wed?.start || null,
      wednesdayTo: data.pickupHours?.wed?.end || null,
      thursdayFrom: data.pickupHours?.thu?.start || null,
      thursdayTo: data.pickupHours?.thu?.end || null,
      fridayFrom: data.pickupHours?.fri?.start || null,
      fridayTo: data.pickupHours?.fri?.end || null,
      saturdayFrom: data.pickupHours?.sat?.start || null,
      saturdayTo: data.pickupHours?.sat?.end || null,
      sundayFrom: data.pickupHours?.sun?.start || null,
      sundayTo: data.pickupHours?.sun?.end || null,
      userIds: data?.locationUsers ? data?.locationUsers : undefined,
      deliveryAreas: data?.deliveryAreas,
      notAtNeighbour: data.notAtNeighbour,
      signature: data.signature,
    };
    if (locationDetail && typeLocation === TYPE_PAGE.EDIT) {
      params.companyId = locationDetail.companyId;

      dispatch(doUpdateLocation(params));
    } else {
      dispatch(doCreateLocation(params));
    }
  };

  const onClickNewLocation = () => {
    setTypeLocation(TYPE_PAGE.NEW);
    setIsModalOpen(true);
  };

  const onClickEditDeliveryAreaLocation = (id) => {
    const location = locations.find((l) => l.id === id);
    setSelectedLocation({
      ...location,
    });
    setDeliveryAreaModalOpen(true);
  };
  const onClickEditOffSchedule = (id) => {
    const location = locations.find((l) => l.id === id);
    setSelectedLocation({
      ...location,
    });

    setOffScheduleOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const onClickEditLocation = (id) => {
    setTypeLocation(TYPE_PAGE.EDIT);
    const location = locations.find((l) => l.id === id);
    let pickupHours = {};
    Object.keys(DAY_NAME)?.forEach((day) => {
      const key = DAY_NAME[day].toLowerCase();
      const start = location[key + 'From'];
      const end = location[key + 'To'];
      pickupHours[day] = {
        start,
        end,
        isValid: true,
      };
    });
    setLocationDetail({
      ...location,
      pickupHours,
    });
    setIsModalOpen(true);
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const cancelModal = () => {
    if (locationDetail) setLocationDetail(undefined);
    setIsModalOpen(false);
  };
  const deliveryAreaModalDone = async () => {
    await dispatch(doGetLocations({ page: currentPage, take: itemsPerPage, ...query }));
    setDeliveryAreaModalOpen(false);
  };

  const AreasTooltip = ({ item }) => {
    return (
      <span>
        {item.deliveryAreaLocations.length
          ? item.deliveryAreaLocations
              .slice() // to overcome immutable state manipulation
              .sort((a, b) => (a.priority < b.priority ? -1 : 1))
              .map((dal) => {
                return (
                  <div key={dal.id}>
                    {dal.priority}. {dal.deliveryArea.name}
                  </div>
                );
              })
          : 'no area connected yet...'}
      </span>
    );
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => onClickEditLocation(record.id)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Tooltip title={<AreasTooltip item={record} />} placement="left">
          <Button type="link" className="edit" onClick={() => onClickEditDeliveryAreaLocation(record.id)}>
            <ReactSVG className="icon" src="/icons/area.svg" />
          </Button>
        </Tooltip>
        <Tooltip title="Edit location pickup schedule and holidays" placement="left">
          <Button type="link" className="edit" onClick={() => onClickEditOffSchedule(record.id)}>
            <CalendarOutlined />
          </Button>
        </Tooltip>
      </div>
    );
  };

  return (
    <Spin spinning={isFetchingCompany || isFetchingLocation}>
      <div className="Content CompaniesPage">
        <div className="ContentHeader">
          <div className="HeaderTitle">Companies</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              <Link to={`/companies/new`} className="new_company">
                <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                  Create New Company
                  <ReactSVG className="icon" src="/icons/plus.svg" />
                </Button>
              </Link>
              <Button className="CreateButton ButtonWhite" size="small" onClick={onClickNewLocation}>
                Create New Location
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Your Companies" key="1">
            <div className="companies-header-table page-header-table">
              <div className="search">
                <Input
                  onChange={handleChangeSearch}
                  onBlur={handleSearch}
                  placeholder="Search"
                  onKeyDown={handleKeyDown}
                  suffix={<img src="/icons/search.svg" />}
                  disabled={isFetchingLocation || isFetchingLocation}
                  value={searchValue}
                />
              </div>
              <div className="button-search">
                <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(!isFilterOpen)}>
                  Filter
                  <ReactSVG className="icon" src="/icons/filter_list.svg" />
                </Button>
              </div>
            </div>
            {isFilterOpen && (
              <CompaniesFilter
                isFetching={isFetchingLocation || isFetchingLocation}
                query={query}
                onSubmit={handleFilter}
              />
            )}
            <div className="ContentMain">
              <CompaniesTable
                onChange={handleTableChange}
                companies={companies}
                dataSource={locations}
                pagination={pagination}
                withAction={withAction}
                rowKey="locationId"
                isFetching={isFetchingLocation}
              />
            </div>
          </Tabs.TabPane>
        </Tabs>
        <DeliveryAreaModal
          open={deliveryAreaModalOpen}
          cancel={() => setDeliveryAreaModalOpen(false)}
          done={deliveryAreaModalDone}
          location={selectedLocation}
          deliveryAreas={deliveryAreas}
        />
        <Modal
          width={552}
          closeIcon={false}
          title={typeLocation === TYPE_PAGE.NEW ? 'Add Location' : 'Edit Location'}
          open={isModalOpen}
          forceRender
        >
          <LocationForm
            handleCancel={cancelModal}
            handleOk={(values) => handleActionLocation(values)}
            companies={companies}
            users={users}
            isModalOpen={isModalOpen}
            location={locationDetail}
            typePage={typeLocation}
            isFetching={isFetchingLocation}
          />
        </Modal>
        <OffSchedule open={offScheduleOpen} close={() => setOffScheduleOpen(false)} location={selectedLocation} />
      </div>
    </Spin>
  );
};

export default withPage(CompaniesPage);
